import React, { useEffect, useRef, useState } from "react";
// The light player rather than the full one, for the reason `GreenCheck` gives:
// this file is eleven shape layers and nothing else — no expressions, no
// effects, no images — and the light build is the same renderer with all of
// that left out.
import lottie, { AnimationItem } from "lottie-web/build/player/lottie_light";
import confirmedAnimation from "../../../assets/svg/enqury/confirmed.json";

/**
 * The mark at the top of the thank-you screen: the tick that draws itself in
 * once the form has been sent.
 *
 * Shared, because both public forms end on it. The two arrive on the same
 * phone from the same practitioner, and a reader who fills in an enquiry and
 * then a pre-consultation should not be able to tell they were built apart.
 *
 * On a loop, the way the dashboard's own `GreenCheck` is — same player, same
 * behaviour, so an animated mark means the same thing wherever it appears.
 * It replays whole rather than looping a segment: the file's flourish overlaps
 * the draw-on, and a piece cut out of the middle restarts with a visible seam.
 *
 * Decorative, and `aria-hidden` for it: what happened is the heading
 * underneath, in words. Nothing here is the only carrier of anything.
 *
 * Two ways it comes out as a plain tick instead, and both are fine: a reader
 * who has asked their system for less movement never gets the draw-on, and a
 * player that fails to load leaves the static mark rather than a hole in the
 * page.
 */

/**
 * The composition is square, so one number sizes it — but the tick does not
 * fill its frame. The ring is drawn a little over half the width, the rest
 * left for the flourish that plays around it, so the box has to be roughly
 * twice the tick you want or it comes out swimming in empty space. The same
 * trap `GreenCheck` documents, at different proportions.
 */
interface ConfirmedMarkProps {
  size?: number;
}

/** What stands in for the animation, and what is behind it until it draws. */
const StaticMark: React.FC<{ size: number }> = ({ size }) => (
  <svg
    viewBox="0 0 48 48"
    width={size}
    height={size}
    fill="none"
    aria-hidden="true"
  >
    <circle cx="24" cy="24" r="21" stroke="#1FA971" strokeWidth="2.5" />
    <path
      d="m15.5 24.5 6 6 11-13"
      stroke="#1FA971"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ConfirmedMark: React.FC<ConfirmedMarkProps> = ({ size = 118 }) => {
  const box = useRef<HTMLSpanElement>(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const host = box.current;
    if (!host) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let animation: AnimationItem | undefined;
    try {
      animation = lottie.loadAnimation({
        container: host,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: confirmedAnimation,
      });
    } catch (error) {
      // A tick is the last thing on this screen worth breaking a confirmation
      // for — the static one below is already standing behind it.
      console.error("confirmed mark failed to load:", error);
      return;
    }

    // Only once the player has actually put something in the box does the
    // static mark go, so there is never a frame with neither. Checked rather
    // than assumed: a player that returns without drawing does not throw, and
    // taking the tick away on its word is how this ends up an empty space.
    if (host.childElementCount > 0) setPlayed(true);

    return () => {
      animation?.destroy();
      setPlayed(false);
    };
  }, []);

  return (
    <span
      className="relative block flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <span
        ref={box}
        aria-hidden
        className="pointer-events-none absolute inset-0"
      />

      {!played && (
        <span aria-hidden className="absolute inset-0 flex items-center justify-center">
          <StaticMark size={Math.round(size * 0.52)} />
        </span>
      )}
    </span>
  );
};

export default ConfirmedMark;
